<script setup>
import { VForm } from 'vuetify/components'
import { mediaUrl } from '@core/utils/mediaUrl'
import { useAppAbility } from '@/plugins/casl/useAppAbility'
import AuthProvider from '@/views/pages/authentication/AuthProvider.vue'
import axios from '@axios'
import { useGenerateImageVariant } from '@core/composable/useGenerateImageVariant'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import { themeConfig } from '@themeConfig'
import {
  emailValidator,
  requiredValidator
} from '@validators'
import moment from 'moment-timezone'

import logoLargeLight from '@/assets/logo.svg'

import { buildProfileUrl, getFullUrl, numberWithCommas, addAndToString, decodeUUID, formatCurrency, displayPhone } from '@/plugins/misc'

// import authV2LoginIllustrationLight from '@/assets/images/pages/auth-v2-login-illustration-light-new.png'
import authV2MaskDark from '@/assets/images/pages/auth-v2-mask-dark.png'
import authV2MaskLight from '@/assets/images/pages/auth-v2-mask-light.png'
import backgroundImage from '@/assets/images/pages/fineDining1.webp'
import {useReservationsStore, useUsersStore} from "@/stores"
import socialRmLogo from "@/assets/social-rm.svg"
import { ref, computed } from 'vue'
import { storeToRefs } from 'pinia'
import RequestPreviewBar from '@core/components/RequestPreviewBar.vue'


const props = defineProps({
  urlSegment: {
    type: String,
    required: true
  },
  all: {
    type: String,
    required: true
  }
})

const route = useRoute()
const router = useRouter()

// Initialize stores
const reservationsStore = useReservationsStore()
const usersStore = useUsersStore()

const {
  confirmingReservation,
  saving
} = storeToRefs(reservationsStore)

const {
  myUser,
  isRunningInStandAlone
} = storeToRefs(usersStore)

// RM-360: /referral/preview/<previewCode> is the member's preview of this page
// — exactly what the restaurant sees, every action shown but none of them
// working (the store never saves a preview; the server refuses it anyway).
const previewMode = ref(props.all == 'preview')
const previewTab = ref('page')
const previewGone = ref(false)

// Before submit the page shows the request as it would go out now.
const isPreviewDraft = computed(() => previewMode.value && confirmingReservation.value?.status === 'Draft')
const previewRestaurantId = computed(() => route.query.restaurantId || confirmingReservation.value?.confirmimgRestaurant?._id || null)

// Vuetify controls inside the page render disabled in preview.
const PREVIEW_DEFAULTS = {
  VBtn: { disabled: true },
  VTextField: { disabled: true },
  VTextarea: { disabled: true },
  VSelect: { disabled: true },
  VCombobox: { disabled: true },
  VAutocomplete: { disabled: true },
  VCheckbox: { disabled: true },
  VRadioGroup: { disabled: true },
  VSwitch: { disabled: true },
}

const loadPreview = async restaurantId => {
  const found = await reservationsStore.getConfirmingReservationPreview(props.urlSegment, restaurantId)
  previewGone.value = !found
  // RM-400 (10/13): an inquiry's preview is the email — there is no response page for it.
  if (found && confirmingReservation.value?.reservationType === 'private') previewTab.value = 'email'
}
const isInquiryPreview = computed(() => previewMode.value && confirmingReservation.value?.reservationType === 'private')

const selectPreviewRestaurant = async restaurantId => {
  if (!restaurantId || restaurantId === previewRestaurantId.value) return
  await router.replace({ query: { ...route.query, restaurantId } })
  await usersStore.showLoadingOverlay()
  await loadPreview(restaurantId)
  await usersStore.hideLoadingOverlay()
}

usersStore.updateEnvironment();

(async function runNow(){
  await usersStore.showLoadingOverlay()
  if(previewMode.value){
    await loadPreview(route.query.restaurantId)
  } else {
    await reservationsStore.getConfirmingReservation(decodeUUID(props.urlSegment))
    await reservationsStore.setupConfirmingListeners()
  }
  await usersStore.hideLoadingOverlay()
})()

const declineMessageToMember = ref('')
const declineMessageHigherSpend = ref('')
const declineMessageDifferentTime = ref('')
const declineMessageToRm = ref('')
const declineType = ref('')

const confirmedReservationSystem = ref(null)

const isShowingSpendDetailsDialog = ref(false)
const spendDialogTab = ref("overall")

const selectedTime = ref(confirmingReservation?.value?.preferredTime)
const confirmedMessageToMember = ref('')
const confirmedMessageToRm = ref('')
const confirmedByName = ref('')

const takeHold = ref(true)
const captureAmount = ref()
const captureMessage = ref('')

const responseTab = ref('Accept')

const postChoiceOne = ref(null)

const showRestaurantSpendLineItems = ref(true)
const showGroupSpendLineItems = ref(true)

// const authThemeImg = useGenerateImageVariant(authV2LoginIllustrationLight, true)
const authThemeMask = useGenerateImageVariant(authV2MaskLight, authV2MaskDark)

const errors = ref({
  email: undefined,
  password: undefined
})
const refVForm = ref()

const isHighlighted = ref(false)
const highlightedDiv = ref(null)

const highlightDiv = () => {
  isHighlighted.value = true
  setTimeout(() => {
    isHighlighted.value = false
  }, 2000) // Duration should match the CSS animation duration
}

const goBack = async ( ) => {
  window.open( `/reservations-review`, '_blank')
}

const onFormSubmit = () => {
  if (previewMode.value) return
  refVForm.value?.validate().then(async ({valid: isValid}) => {
    if (isValid){
      await usersStore.showLoadingOverlay()
      if(responseTab.value == "Accept"){
        confirmingReservation.value.newStatus = "Accepted"
        confirmingReservation.value.confirmedRestaurant = thisRestaurant.value
        confirmingReservation.value.confirmedTime = selectedTime.value
        confirmingReservation.value.confirmedMessageToRm = confirmedMessageToRm.value
        confirmingReservation.value.confirmedMessageToMember = confirmedMessageToMember.value
        confirmingReservation.value.confirmedReservationSystem = confirmedReservationSystem.value
        confirmingReservation.value.confirmedByName = confirmedByName.value
        if(thisRestaurantHoldData?.value?.holdAmountDollars && takeHold.value){
          confirmingReservation.value.stripeHoldConfirmedAmount = thisRestaurantHoldData?.value?.holdAmountDollars
        } else if (confirmingReservation.value.preferredMemberBypassApplied || confirmingReservation.value.stripeHoldType === 'none'){
          confirmingReservation.value.stripeHoldConfirmedAmount = 0
        } else{
          confirmingReservation.value.stripeHoldConfirmedAmount = 0
          confirmingReservation.value.releaseHold = true
        }
      }
      else if(responseTab.value == "Decline"){
        for(let i in confirmingReservation.value.declines){
          if(confirmingReservation.value.confirmationUuids[i] == decodeUUID(props.urlSegment)){
            confirmingReservation.value.declines[i] = {declineMessageToMember: declineMessageToMember.value, declineMessageHigherSpend: declineMessageHigherSpend.value, declineMessageDifferentTime: declineMessageDifferentTime.value,declineMessageToRm: declineMessageToRm.value, declineDate: new Date()}
          }
        }
      }
      await reservationsStore.saveConfirmingReservation()
      await usersStore.hideLoadingOverlay()
    }
  })
}

// Accept fields are always open (the desired outcome — no extra steps for the
// restaurant). Choosing "Accept" vs an alternate/decline is determined by which
// button is pressed: it sets responseTab (which onFormSubmit branches on, and
// which gates the per-path required-field rules) before validating.
const submitAs = async action => {
  responseTab.value = action
  await nextTick()
  onFormSubmit()
}

const captureHold = async ( ) => {
  await usersStore.showLoadingOverlay()
  confirmingReservation.value.captureHold = true
  confirmingReservation.value.captureAmount = captureAmount.value
  confirmingReservation.value.stripeHoldCapturedMessage = captureMessage.value
  postChoiceOne.value = null

  await reservationsStore.saveConfirmingReservation()
  await usersStore.hideLoadingOverlay()
}

const captureHoldNoShow = async ( ) => {
  await usersStore.showLoadingOverlay()
  confirmingReservation.value.captureHold = true
  confirmingReservation.value.captureAmount = captureAmount.value
  confirmingReservation.value.stripeHoldCapturedMessage = "No-show"
  confirmingReservation.value.newStatus = "Canceled"
  postChoiceOne.value = null

  await reservationsStore.saveConfirmingReservation()
  await usersStore.hideLoadingOverlay()
}

const captureHoldCanceled = async ( ) => {
  await usersStore.showLoadingOverlay()
  confirmingReservation.value.captureHold = true
  confirmingReservation.value.captureAmount = captureAmount.value
  postChoiceOne.value = null

  await reservationsStore.saveConfirmingReservation()
  await usersStore.hideLoadingOverlay()
}

const releaseHold = async ( ) => {
  await usersStore.showLoadingOverlay()
  confirmingReservation.value.releaseHold = true
  postChoiceOne.value = null
  await reservationsStore.saveConfirmingReservation()
  await usersStore.hideLoadingOverlay()
}

const releaseHoldAndCancel = async ( ) => {
  await usersStore.showLoadingOverlay()
  confirmingReservation.value.releaseHold = true
  confirmingReservation.value.newStatus = "Canceled"
  postChoiceOne.value = null
  await reservationsStore.saveConfirmingReservation()
  await usersStore.hideLoadingOverlay()
}

const cancel = async ( ) => {
  await usersStore.showLoadingOverlay()
  confirmingReservation.value.newStatus = "Canceled"
  postChoiceOne.value = null
  await reservationsStore.saveConfirmingReservation()
  await usersStore.hideLoadingOverlay()
}

const checkCaptureAmount = async ( ) => {
  if(captureAmount.value && captureAmount.value >  confirmingReservation.value.stripeHoldConfirmedAmount){
    captureAmount.value =  confirmingReservation.value.stripeHoldConfirmedAmount
  }

}


//copy pasted that need to be centralized?
const generateTimeRange = (startTimeMoment, endTimeMoment, except) => {
  let returnArray = []
  let momentProcessing = moment(startTimeMoment)
  while(momentProcessing.isSameOrBefore(endTimeMoment)){
    let theTime = momentProcessing.format("h:mma")
    if(theTime != except){
      returnArray.push(theTime)
    }
    momentProcessing.add(15,"minutes")
  }
  
  return returnArray
}

//returns actual spend if we have it, otherwise estimate it
const formattedSpend = booking => {
  if(booking.actualSpendTotal)
    return '$'+numberWithCommas(booking.actualSpendTotal)+' (actual)'
  if(booking.stripeHoldMaxAmount)
    return '$'+numberWithCommas(booking.stripeHoldMaxAmount)
  
  return '$'+numberWithCommas(booking.minSpendPerPerson*booking.partySize*1.32)
}

const afterReservationStartTime = computed(() => {
  let reservationMoment = moment.tz(confirmingReservation.value.date + ' ' + (confirmingReservation.value.confirmedTime ? confirmingReservation.value.confirmedTime : confirmingReservation.value.preferredTime), 'YYYY-MM-DD hh:mm:a', confirmingReservation.value.timeZone.identifier)
  
  return (confirmingReservation.value.status == "Accepted" && moment().isAfter(reservationMoment))
})

let timeOptions = computed(() => {
  let startTimeMoment
  let endTimeMoment

  if(confirmingReservation.value.reservationType == "private"){
    startTimeMoment = moment('0:00am', "h:mma")
    endTimeMoment = moment(startTimeMoment).add(23, 'hours').add(45, 'minutes')
  }
  else{
    console.log("timezone", confirmingReservation.value.timeZone.identifier)
    startTimeMoment = moment.tz(confirmingReservation.value.date + ' ' + confirmingReservation.value.earliestTime, 'YYYY-MM-DD h:mma', confirmingReservation.value.timeZone.identifier)

    console.log("startTimeMoment", startTimeMoment)
    let earliestBookingMoment = moment.tz(confirmingReservation.value.timeZone.identifier) //now
    console.log("earliestBookingMoment", earliestBookingMoment)
    if (
      moment.tz(confirmingReservation.value.date, confirmingReservation.value.timeZone.identifier)
        .isSame(moment(), "day") &&
      startTimeMoment.isBefore(earliestBookingMoment)
    ) {
      console.log("HERE")
      startTimeMoment = earliestBookingMoment
        .minutes(Math.ceil(earliestBookingMoment.minutes() / 15) * 15)
        .seconds(0)
        .milliseconds(0)
    }

    endTimeMoment = moment(confirmingReservation.value.latestTime, "h:mma")
    endTimeMoment = moment.tz(confirmingReservation.value.date + ' ' + confirmingReservation.value.latestTime, 'YYYY-MM-DD h:mma', confirmingReservation.value.timeZone.identifier)
    console.log("endTimeMoment", endTimeMoment)
  }
  
  return generateTimeRange(startTimeMoment, endTimeMoment)

  // return generateTimeRange(startTimeMoment, endTimeMoment, confirmingReservation.value.preferredTime)
})

let thisRestaurantSpendShareSettings = computed(() => {
  if(confirmingReservation?.value?.restaurantSpendShareSettings && confirmingReservation.value.restaurantSpendShareSettings.length > 0){
    return confirmingReservation.value.restaurantSpendShareSettings[thisRestaurantIndex.value]
  }
  
  return null
})

let thisRestaurantHoldData = computed(() => {
  if(confirmingReservation?.value?.requestedRestaurantsArray && confirmingReservation.value.requestedRestaurantsArray.length > 0){
    return confirmingReservation.value.stripeRestaurantHoldData[thisRestaurantIndex.value]
  }
  
  return null
})

let thisRestaurant = computed(() => {
  if(confirmingReservation?.value?.requestedRestaurantsArray && confirmingReservation.value.requestedRestaurantsArray.length > 0){
    return confirmingReservation.value.requestedRestaurantsArray[thisRestaurantIndex.value]
  }
  
  return null
})

let thisRestaurantIndex = computed(() => {
  if(confirmingReservation?.value?.requestedRestaurantsArray && confirmingReservation.value.requestedRestaurantsArray.length > 0){
    for(let i in confirmingReservation.value.requestedRestaurantsArray){
      if(confirmingReservation.value.requestedRestaurantsArray[i] == confirmingReservation?.value?.confirmimgRestaurant.name){
        return i
      }
    }
  }
  
  return null
})

let thisRestaurantDeclinedAt = computed(() => {
  // The preview carries no confirm code: its restaurant is the one on screen.
  if(previewMode.value){
    const decline = confirmingReservation.value?.declines?.[thisRestaurantIndex.value]
    if(decline?.declineDate) return decline.declineDate

    return decline ? true : null
  }
  if(confirmingReservation?.value?.requestedRestaurantsArray && confirmingReservation.value.requestedRestaurantsArray.length > 0){
    for(let i in confirmingReservation.value.requestedRestaurantsArray){
      if(confirmingReservation.value.confirmationUuids[i] == decodeUUID(props.urlSegment)){
        if(confirmingReservation?.value?.declines?.[i]?.declineDate){
          return confirmingReservation.value.declines[i].declineDate
        }
        if(confirmingReservation.value.declines[i] !== null){
          return true
        }
      }
    }
  }
  
  return null
})

let confirmedByDifferentRestaurant = computed(() => {
  return confirmingReservation.value.confirmedRestaurant && confirmingReservation.value.confirmedRestaurant != thisRestaurant.value
})

let confirmedByThisRestaurant = computed(() => {
  return confirmingReservation.value.confirmedRestaurant && confirmingReservation.value.confirmedRestaurant == thisRestaurant.value
})

let responseAllowed = computed(() => {
  return confirmingReservation.value && !confirmingReservation.value.confirmedRestaurant && !thisRestaurantDeclinedAt.value && (confirmingReservation.value.status == "Awaiting Response from Restaurant" || confirmingReservation.value.status == "In Review" || confirmingReservation.value.status == "Member Action")
})

let otherRestaurants = computed(() => {
  if(confirmingReservation.value.requestedRestaurantsArray.length > 0){
    let returnString = ""
    for(let i in confirmingReservation.value.requestedRestaurantsArray){
      if(confirmingReservation.value.requestedRestaurantsArray[i] != confirmingReservation?.value?.confirmimgRestaurant.name){
        returnString = returnString + confirmingReservation.value.requestedRestaurantsArray[i] + ", "
      }
    }
    
    return returnString.slice(0, -2)
  }
  
  return null
})

let restaurantMemberSpend = computed(() => {
  return confirmingReservation.value.confirmingRestaurantSpendHistory.restaurantMemberSpend
})

let groupMemberSpend = computed(() => {
  return confirmingReservation.value.confirmingRestaurantSpendHistory.groupMemberSpend
})

let restaurantNetworkSpend = computed(() => {
  return confirmingReservation.value.confirmingRestaurantSpendHistory.restaurantNetworkSpend
})

let groupNetworkSpend = computed(() => {
  return confirmingReservation.value.confirmingRestaurantSpendHistory.groupNetworkSpend
})

let overallSpend = computed(() => {
  return confirmingReservation.value.confirmingRestaurantSpendHistory.overallSpend
})

let higherSpendLabel = computed(() => {
  return confirmingReservation.value.partySize > 1 ? "F&B min pp | F&B total" : "F&B minimum"
})

let minSpendOptions = computed(() => {
  let returnArray = [
    {text: '$3,000pp', value: 3000},
    {text: '$2,500pp', value: 2500},
    {text: '$2,000pp',value: 2000},
    {text: '$1,500pp', value: 1500},
    {text: '$1,000pp', value: 1000},
    {text: '$900pp', value: 900},
    {text: '$800pp',value: 800},
    {text: '$750pp', value: 750},
    {text: '$700pp', value: 700},
    {text: '$650pp', value: 650},
    {text: '$600pp',value: 600},
    {text: '$550pp', value: 550},
    {text: '$500pp', value: 500},
    {text: '$450pp', value: 450},
    {text: '$400pp',value: 400},
    {text: '$375pp', value: 375},
    {text: '$350pp', value: 350},
    {text: '$300pp', value: 300},
    {text: '$250pp',value: 250},
    {text: '$200pp', value: 200},
    {text: '$175pp', value: 175},
    {text: '$150pp', value: 150},
    {text: '$125pp',value: 125},
    {text: '$100pp', value: 100},
    {text: '$75pp', value: 75},
    {text: '$50pp', value: 50}
  ]

  //reverse order of returnArray fully written out
  returnArray = returnArray.reverse()

  // Get current minimum spend
  const currentMinSpend = confirmingReservation.value.minSpendPerPerson || 0

  // Filter the array to include only items where value is greater than currentMinSpend
  returnArray = returnArray.filter(item => item.value > currentMinSpend)

  if(confirmingReservation.value.partySize > 1){
    returnArray = returnArray.map(option => {
      let totalSpend = option.value * (confirmingReservation.value.partySize || 1) // Default party size to 1 if not set
      
      return {
        ...option,
        text: `$${option.value.toLocaleString()}pp | $${totalSpend.toLocaleString()}`
      }
    })
  }
  returnArray = returnArray.map(item => ({
    ...item,
    value: item.text
  }))

  return returnArray
})

//watches for changes to the alternateRestaurants and sets changesMade to true
watch(
  () => confirmingReservation?.value?.confirmimgRestaurant,
  () => {
    if(confirmingReservation?.value?.confirmimgRestaurant){
      confirmedReservationSystem.value = confirmingReservation?.value?.confirmimgRestaurant?.generalReservationPlatform
    }
  },
  {deep: true}
)

watch(
  () => confirmingReservation?.value?.preferredTime,
  () => {
    console.log("setting confirmingReservation.value.confirmedTime", confirmingReservation?.value?.preferredTime)
    if(!selectedTime.value){
      selectedTime.value = confirmingReservation?.value?.preferredTime
    }
  }

  // {immediate: true}
)


const copyPageURL = () => {
  let textArea = document.createElement("textarea")
  textArea.value = window.location.href
  document.body.prepend(textArea)
  textArea.focus()
  textArea.select()

  try {
    let successful = document.execCommand('copy')
    let msg = successful ? 'successful' : 'unsuccessful'
    console.log('Fallback: Copying text command was ' + msg)
  } catch (err) {
    console.error('Fallback: Oops, unable to copy', err)
  }

  document.body.removeChild(textArea)
}

const scrollToBottom = () => {
  setTimeout(() => {
    window.scrollTo({
      top: document.body.scrollHeight,
      behavior: 'smooth',
    })
  }, 0)
}
</script>

<template>
  <div class=" content-wrapper pa-2">
    <div>
      <div class="flex justify-space-between align-center">
        <VBtn
          v-if="isRunningInStandAlone && !previewMode"
          density="compact"
          class="bg-light-secondary my-6"
          icon="mdi-arrow-left"
          @click="goBack"
        />
        <VBtn
          v-if="myUser?.userLevel === 'admin' && isRunningInStandAlone && !previewMode"
          class="ml-auto"
          variant="text"
          @click="copyPageURL"
        >
          <VIcon class="mr-2">
            mdi-link
          </VIcon> Copy Page Url
        </VBtn>
      </div>

      <RequestPreviewBar
        v-if="previewMode && confirmingReservation"
        v-model:tab="previewTab"
        :preview-code="props.urlSegment"
        :restaurants="confirmingReservation.previewRestaurants || []"
        :restaurant-id="previewRestaurantId"
        :submitted="!isPreviewDraft"
        :inquiry="isInquiryPreview"
        @select-restaurant="selectPreviewRestaurant"
      />
      <p
        v-if="previewMode && previewGone"
        class="text-body-1 mt-4"
      >
        This request was deleted, so its preview link no longer works
      </p>

      <VDefaultsProvider :defaults="previewMode ? PREVIEW_DEFAULTS : {}">
        <fieldset
          v-show="!previewMode || previewTab === 'page'"
          :disabled="previewMode"
          class="preview-fieldset"
        >
      <div>
        <div class="ps-0 pt-0 text-xs">
          <div
            v-if="confirmingReservation"
            class="flex items-center gap-2 mt-0"
          >
            <a
              href="https://reserverm.com"
              target="_blank"
              rel="noopener"
              class="d-flex align-self-center"
              style="line-height: 0;"
            >
              <img
                width="30"
                :src="logoLargeLight"
              >
            </a>
            <div
              class="confirm-form-title text-left align-self-center"
              style="font-size: 1.25em;"
            >
              Reservation request<span v-if="confirmingReservation?.owner"> from<br><a
                :href="`/m/${confirmingReservation.owner?.profileId}`"
                target="_blank"
                style="color: inherit; text-decoration: underline;"
              >{{ confirmingReservation.owner.fullName }}</a><VIcon
                v-if="confirmingReservation.owner?.verified"
                size="20"
                class="verified-icon ml-1"
                style="margin: 0px; margin-top: -3px;"
              >material-symbols:verified</VIcon> ({{ confirmingReservation.owner.membershipType }})</span>
            </div>
          </div>
          <div
            v-if="confirmingReservation && confirmingReservation.submittedAt"
            class="text-left text-xs mt-1 mb-0 fine-print"
          >
            submitted {{ moment(confirmingReservation.submittedAt).tz(confirmingReservation.timeZone.identifier).format("M/DD [at] h:mma") }}
          </div>
          <div
            v-if="confirmingReservation"
            class="text-sm mt-1 mb-0 pa-0 "
          >
            <b>Preferred Restaurant: </b>{{ confirmingReservation.confirmimgRestaurant.name }} ({{ confirmingReservation.market.split(",")[0] }})<span v-if="otherRestaurants">, {{ addAndToString(otherRestaurants) }}</span>
          </div>

          <div
            v-if="confirmingReservation"
            class="text-left text-xs mt-0 fine-print"
          >
            <span v-if="thisRestaurantDeclinedAt === true">declined<br></span>
            <span v-else-if="thisRestaurantDeclinedAt">declined {{ moment(thisRestaurantDeclinedAt).tz(confirmingReservation.timeZone.identifier).format("M/DD [at] h:mma") }} {{ confirmingReservation.timeZone.abbreviation }}<br></span>
            <span v-if="thisRestaurantDeclinedAt">Thanks for your consideration!</span>






            <!--                  <span v-if="confirmingReservation.status =='Submitted'" >Submitted {{ moment(confirmingReservation.submittedAt).tz(confirmingReservation.timeZone.identifier).format("M/DD [at] h:mma") }} {{confirmingReservation.timeZone.abbreviation}}</span> -->
            <!--                  <h3 v-if="confirmingReservation.status =='Awaiting Response from Restaurant'">Status: Awaiting Review</h3> -->
            <!--                  <span v-if="confirmingReservation.status =='Awaiting Response from Restaurant'">Submitted {{ moment(confirmingReservation.submittedAt).tz(confirmingReservation.timeZone.identifier).format("M/DD [at] h:mma") }} {{confirmingReservation.timeZone.abbreviation}}</span> -->
            <!--                  <span v-if="confirmingReservation.status =='In Review'">In Review</span> -->
            <span v-if="confirmingReservation.status =='Member Action'">Member Action Required</span>
            <!--              <span v-if="!thisRestaurantDeclinedAt && (confirmingReservation.status =='Awaiting Response from Restaurant' || confirmingReservation.status =='Submitted' || confirmingReservation.status =='In Review' || confirmingReservation.status =='Member Action') && confirmingReservation.requestExpirationTime">expires {{ moment(confirmingReservation.requestExpirationTime).tz(confirmingReservation.timeZone.identifier).format("M/DD [at] h:mma") }} {{confirmingReservation.timeZone.abbreviation}}<br></span> -->
            <span v-if="confirmingReservation.status =='Rescinding' || confirmingReservation.status =='Rescinded'">Submitted {{ moment(confirmingReservation.submittedAt).tz(confirmingReservation.timeZone.identifier).format("M/DD [at] h:mma") }} {{ confirmingReservation.timeZone.abbreviation }}<br>
              Member rescinded {{ moment(confirmingReservation.lastStatusChangeAt).tz(confirmingReservation.timeZone.identifier).format("M/DD [at] h:mma") }} {{ confirmingReservation.timeZone.abbreviation }}</span>
            <span v-if="confirmingReservation.status =='Canceling'">Canceled</span>
            <span v-if="confirmingReservation.status =='Canceled'">canceled {{ moment(confirmingReservation.lastStatusChangeAt).tz(confirmingReservation.timeZone.identifier).format("M/DD [at] h:mma") }} {{ confirmingReservation.timeZone.abbreviation }} by {{ confirmingReservation.owner.fullName }}<br>
              The member wishes to cancel this reservation, please process the cancellation if possible.</span>
            <!--                  <span v-if="confirmedByThisRestaurant"> -->
            <!--                      Accepted <span v-if="confirmingReservation.acceptedAt">{{ moment(confirmingReservation.acceptedAt).tz(confirmingReservation.timeZone.identifier).format("M/DD [at] h:mma") }} {{confirmingReservation.timeZone.abbreviation}} by {{confirmingReservation.confirmedByName }}</span> -->
            <!--                    </span> -->

            <span v-if="confirmingReservation.status =='Accepted'">
              <span v-if="confirmedByThisRestaurant">
                accepted <span v-if="confirmingReservation.acceptedAt">{{ moment(confirmingReservation.acceptedAt).tz(confirmingReservation.timeZone.identifier).format("M/DD [at] h:mma") }} {{ confirmingReservation.timeZone.abbreviation }} by {{ confirmingReservation.confirmedByName }}</span>
              </span>
              <span v-else-if="confirmedByDifferentRestaurant && !thisRestaurantDeclinedAt">
                <VAlert
                  class="ma-2"
                  type="info"
                >
                  Referral accepted by another restaurant
                </VAlert>

              </span>

            </span>

            <!--                  <span v-if="confirmingReservation.status =='Not Available'">Not Available</span> -->
            <span v-if="confirmingReservation.status =='No Response' && !confirmingReservation.expiredAt">No response from restaurant</span>
            <span v-if="confirmingReservation.status =='No Response' && confirmingReservation.expiredAt">expired {{ moment(confirmingReservation.expiredAt).tz(confirmingReservation.timeZone.identifier).format("M/DD [at] h:mma") }} {{ confirmingReservation.timeZone.abbreviation }}</span>
            <span v-if="confirmingReservation.status =='Declined'">Declined by restaurant</span>
            <!--                  <span v-if="!thisRestaurantDeclinedAt &&  confirmingReservation.status =='Awaiting Response from Restaurant'" ><b>Thanks for your review!</b></span> -->
          </div>
          <div
            v-if="isPreviewDraft"
            class="text-left text-xs"
          >
            <span>submitted {{ moment().tz(confirmingReservation.timeZone.identifier).format("M/DD [at] h:mma") }} - expires {{ moment(confirmingReservation.requestExpirationTime).tz(confirmingReservation.timeZone.identifier).format("M/DD [at] h:mma") }} {{ confirmingReservation.timeZone.abbreviation }}</span>
          </div>

          <!--            <div style="line-height: 1.1em"> -->
          <!--            <i><a href="https://reserverm.com" target="_blank" class=" text-xs"> -->
          <!--              <div>trusted by artists, athletes, & execs/ceos</div> -->
          <!--              <div class="mt-1">loved by their favorite restaurants</div> -->
          <!--            </a></i> -->
          <!--            </div> -->
          <!--            <div class=" mt-0" v-if="confirmingReservation"> -->
          <!-- &lt;!&ndash;              <h2 v-if="confirmingReservation.status =='Rescinding'" >Rescinded by member, hope to send another one soon</h2>&ndash;&gt; -->
          <!-- &lt;!&ndash;              <h2 v-if="confirmingReservation.status =='Rescinded'" >Rescinded by member, hope to send another one soon</h2>&ndash;&gt; -->
          <!--            </div> -->

          <div
            v-if="confirmingReservation"
            :class="{ strikethrough: (thisRestaurantDeclinedAt || confirmedByDifferentRestaurant || confirmingReservation.status =='Rescinding' || confirmingReservation.status =='Rescinded' || confirmingReservation.status =='No Response' || confirmingReservation.status =='Canceled') }"
          >
            <!--              <div class=" mt-0 mb-0 pa-0 "><b>Preferred Restaurant(s): </b>{{ confirmingReservation.confirmimgRestaurant.name}} ({{ confirmingReservation.market.split(",")[0] }})<span v-if="otherRestaurants">, {{addAndToString(otherRestaurants)}}</span></div> -->
            <!--              <div v-if="confirmingReservation?.confirmimgRestaurant?.group">{{(confirmingReservation?.confirmimgRestaurant?.group ? '('+confirmingReservation.confirmimgRestaurant.group +')' : '')}}</div> -->
            <!--              <div class=" mt-0 mb-0 pa-0 text-xs">(view/claim page here: <a :href="`/r/${confirmingReservation.confirmimgRestaurant.profileId}`" target="_blank">reserverm.com/r/{{confirmingReservation.confirmimgRestaurant.profileId}})</a></div> -->

            <!--              <h2 class="reservation-item-restaurant-name mt-2"> {{ confirmingReservation.occasion}}</h2> -->

            <div class=" mt-2">
              <div
                v-if="confirmingReservation.status =='Canceled'"
                class=" mt-0 mb-0 pa-0 text-sm"
              >
                <u>Accepted Reservation Details</u>
              </div>
              <div class="detail-line">
                <b>Date:</b> {{ (confirmingReservation.date ? moment(confirmingReservation.date).format("dddd, M/D") : "No date selected") }}
              </div>
              <div class="detail-line">
                <b>Party Size:</b> {{ confirmingReservation.partySize }}
              </div>
              <div class="detail-line">
                <b>Preferred Time:</b> {{ confirmingReservation.confirmedTime ? confirmingReservation.confirmedTime : confirmingReservation.preferredTime }}
              </div>
              <div
                v-if="!confirmingReservation.confirmedTime && confirmingReservation.earliestTime && confirmingReservation.latestTime"
                class="detail-line"
              >
                <b>Alternate Start Time if {{ confirmingReservation.preferredTime }} not available:</b> {{ confirmingReservation.earliestTime }}-{{ confirmingReservation.latestTime }}
              </div>
              <!--                <div class="" -->
              <!--                v-if="confirmingReservation.confirmedTime"> -->
              <!--                  <VIcon -->
              <!--                    start -->
              <!--                    size="18" -->
              <!--                    class="reservation-response-item-detail-icon" -->
              <!--                    icon="mdi-clock-outline" -->
              <!--                  /> -->
              <!--                  <span>{{ confirmingReservation.confirmedTime }} {{confirmingReservation.timeZone.abbreviation}}</span> -->
              <!--                </div> -->
              <!--                <template v-else> -->
              <!--                  <div class="" -->
              <!--                       v-if="confirmingReservation.earliestTime"> -->
              <!--                    <VIcon -->
              <!--                      start -->
              <!--                      size="18" -->
              <!--                      class="reservation-response-item-detail-icon" -->
              <!--                      icon="mdi-clock-outline" -->
              <!--                    /> -->
              <!--                    <span>Preferred Time: {{ confirmingReservation.preferredTime }}{{(confirmingReservation ? ' '+ confirmingReservation.timeZone.abbreviation : '')}}</span> -->
              <!--                  </div> -->
              <!--                  <div class="" -->
              <!--                       v-if="confirmingReservation.earliestTime"> -->
              <!--                    <VIcon -->
              <!--                      start -->
              <!--                      size="18" -->
              <!--                      class="reservation-response-item-detail-icon" -->
              <!--                      icon="mdi-clock-outline" -->
              <!--                    /> -->
              <!--                    <span>Time Range if Preferred N/A: {{ confirmingReservation.earliestTime.slice(0,-2) }}-{{ confirmingReservation.latestTime}}{{(confirmingReservation ? ' '+ confirmingReservation.timeZone.abbreviation : '')}}</span> -->
              <!--                  </div> -->
              <!--                  <div class="" v-else> -->
              <!--                    <VIcon -->

              <!--                      start -->
              <!--                      size="18" -->
              <!--                      class="reservation-response-item-detail-icon" -->
              <!--                      icon="mdi-clock-alert-outline" -->
              <!--                    /> -->
              <!--                    <span>{{ confirmingReservation.preferredTime }} {{confirmingReservation.timeZone.abbreviation}} (Preferred Start Time)</span> -->
              <!--                  </div> -->
              <!--                </template> -->
              <!--                <div class=""> -->
              <!--                  <VIcon -->

              <!--                    start -->
              <!--                    size="18" -->
              <!--                    class="reservation-response-item-detail-icon" -->
              <!--                    icon="mdi-users-outline" -->
              <!--                  /> -->
              <!--                  <span>{{ confirmingReservation.partySize }} {{parseInt(confirmingReservation.partySize) > 1?'people':'person'}}</span> -->
              <!--                </div> -->
              <div
                v-if="confirmingReservation.flexibility && confirmingReservation.reservationType == 'private'"
                class="mt-1"
              >
                <span><b>Flexible on Date or Time:</b> {{ confirmingReservation.flexibility }}</span>
              </div>
              <div
                v-if="confirmingReservation.occasion"
                class="detail-line"
              >
                <b>Occasion:</b> {{ confirmingReservation.occasion }} <span v-if="confirmingReservation.occasionDetails"> - {{ confirmingReservation.occasionDetails }}</span>
              </div>
              <div
                v-if="confirmingReservation.additionalNotes"
                class="detail-line"
                style="white-space: pre-line;"
              >
                <b>Notes:</b> {{ confirmingReservation.additionalNotes }}
              </div>
              <div
                v-if="confirmingReservation.seatingPreference && confirmingReservation.reservationType == 'private'"
                class=""
              >
                <span><b>Seating Preference:</b> {{ confirmingReservation.seatingPreference }}</span>
              </div>
              <div
                v-if="confirmingReservation.specialRequests"
                class="detail-line"
              >
                <b>Notes:</b> {{ confirmingReservation.specialRequests }}
              </div>

              <div
                v-if="confirmingReservation.hostAffiliated"
                class=""
              >
                <b>Affiliation:</b> {{ confirmingReservation.hostAffiliation }}
              </div>


              <div v-if="confirmingReservation?.preferredMemberBypassApplied">
                <div class="mt-0 mb-0 pa-0 ">
                  <b>Preferred Member Request</b>
                </div>
                <div class="ml-2">
                  This request is from a member that <a
                    v-if="confirmingReservation?.preferredMemberAdminProfileId"
                    :href="`/m/${confirmingReservation.preferredMemberAdminProfileId}`"
                    target="_blank"
                    rel="noopener"
                  >{{ confirmingReservation?.preferredMemberAdminName || 'an admin' }}</a><span v-else>{{ confirmingReservation?.preferredMemberAdminName || 'an admin' }}</span> has marked as Preferred on RM
                </div>
              </div>
              <div v-else>
                <div class="detail-line">
                  <b>Guaranteed F&amp;B Spend:</b> ${{ numberWithCommas(thisRestaurantHoldData?.foodAndBeverageSpend) }} (${{ numberWithCommas(confirmingReservation.minSpendPerPerson) }}pp) - cc on hold for total + 20% tip &amp; tax - RM takes no fees
                </div>
                <!--                  <span v-if="confirmingReservation.status =='Awaiting Response from Restaurant'"><b>F&B: ${{numberWithCommas(thisRestaurantHoldData?.foodAndBeverageSpend)}} ${{ numberWithCommas(perPersonHoldAmount)  }}</b> (${{ numberWithCommas(confirmingReservation.minSpendPerPerson) }}pp) - pre tax & tip</span> -->
                <!--                  <span v-else><b>F&B: ${{ numberWithCommas(confirmingReservation.partySize * confirmingReservation.minSpendPerPerson)  }} (${{ numberWithCommas(confirmingReservation.minSpendPerPerson) }}pp)</b></span> -->
                <!--                  <div><b>Tip: ${{numberWithCommas(thisRestaurantHoldData?.gratuityAmount || (confirmingReservation.partySize * confirmingReservation.minSpendPerPerson*0.20))}}</b></div> -->
                <!--                  <div><b>Tax (estimated): ${{numberWithCommas(thisRestaurantHoldData?.tax || (confirmingReservation.partySize * confirmingReservation.minSpendPerPerson*0.12))}}</b></div> -->

                <div
                  v-if="confirmingReservation.stripeHoldType == 'fullWithCharge' || thisRestaurantHoldData?.type == 'fullWithCharge'"
                  class="ml-2"
                >
                  <div v-if="confirmingReservation?.stripeHoldCapturedAmount">
                    <span><b>Charged: ${{ numberWithCommas((confirmingReservation?.stripeHoldCapturedAmount).toFixed(2)) }}</b></span><br>
                    <span>This full amount less credit card processing fees, will be transferred to you within 5 days after the reservation.</span>
                  </div>
                  <div v-else-if="confirmingReservation?.stripeHoldConfirmedAmount === 0">
                    <span><b>Hold Waived</b></span>
                  </div>
                  <div v-else-if="confirmingReservation?.stripeHoldReleasedAt">
                    <span><b>Hold Released</b></span>
                  </div>
                  <div v-else-if="confirmingReservation?.stripeHoldConfirmedAmount">
                    <span><b>On Hold: ${{ numberWithCommas((confirmingReservation?.stripeHoldConfirmedAmount).toFixed(2)) }}</b></span><br>
                    <span>We will charge the full amount unless member cancels within the cancellation window. This full amount, less credit card processing fees, will be transferred to you within 5 days after the reservation.</span>
                  </div>
                  <div v-else-if="thisRestaurantHoldData?.holdAmountDollars">
                    <span><b>💳 Prepaid: ${{ numberWithCommas(thisRestaurantHoldData?.holdAmountDollars) }}</b></span><br>
                    <span>Once confirmed below RM will place a hold on the member's card for this full amount, and will subsequently charge unless member cancels within the cancellation window. This full amount will be transferred to you within 5 days after the reservation.</span>
                  </div>
                  <div
                    v-if="confirmingReservation?.stripeHoldCapturedMessage"
                    class="reservation-item-spend"
                  >
                    {{ confirmingReservation.stripeHoldCapturedMessage }}
                  </div>
                </div>

                <div
                  v-else
                  class=""
                >
                  <div v-if="thisRestaurantHoldData?.holdAmountDollars && confirmingReservation?.stripeHoldCapturedAmount">
                    <span><b>💳 Hold Charged: ${{ numberWithCommas((confirmingReservation?.stripeHoldCapturedAmount).toFixed(2)) }}</b></span><br>
                    <span>This amount, less CC processing fees, will be transferred to you within 5 days.</span>
                  </div>
                  <div v-else-if="thisRestaurantHoldData?.holdAmountDollars && confirmingReservation?.stripeHoldConfirmedAmount === 0">
                    <span><b>💳 Hold Waived</b></span>
                  </div>
                  <div v-else-if="thisRestaurantHoldData?.holdAmountDollars && confirmingReservation?.stripeHoldReleasedAt">
                    <span><b>💳 Hold Released</b></span>
                  </div>
                  <div v-else-if="thisRestaurantHoldData?.holdAmountDollars && confirmingReservation?.stripeHoldConfirmedAmount">
                    <span class="mr-1"><b>Total on Hold: ${{ numberWithCommas((confirmingReservation?.stripeHoldConfirmedAmount).toFixed(2)) }}</b></span>
                  </div>
                  <div
                    v-else-if="thisRestaurantHoldData?.holdAmountDollars"
                    class="ml-2"
                  >
                    <span><i>Member credit card hold authorized for Total - if not met, restaurant can choose to receive remainder within 24 hours.</i></span>
                    <!--                      <rm-tool-tip> -->
                    <!--                        <template #text> -->
                    <!--                          <span class="text-primary">how it works</span> -->
                    <!--                        </template> -->
                    <!--                        <template #description> -->
                    <!--                          <span>Hold will be released back to member post reservation after they meet/exceed F&B spend. -->
                    <!--                                If spend is not met, restaurant can notify RM within 24 hours of reservation. -->
                    <!--                                Remainder will be sent to restaurant within 5 days. Restaurants can waive minimum/hold at their sole discretion.</span> -->
                    <!--                        </template> -->
                    <!--                      </rm-tool-tip> -->
                    <br>
                  </div>
                  <div v-else>
                    <span class="mr-1"><b>Total: ${{ numberWithCommas((confirmingReservation.partySize * confirmingReservation.minSpendPerPerson) + (confirmingReservation.partySize * confirmingReservation.minSpendPerPerson * 0.2) + (confirmingReservation.partySize * confirmingReservation.minSpendPerPerson * 0.12)) }}</b></span> -
                    <RmToolTip>
                      <template #text>
                        <span class="text-primary">how it works</span>
                      </template>
                      <template #description>
                        <span>Member has authorized to be charged the full F&B if not met. Credit card hold can be taken in advance if preferred. Restaurants can waive minimum at their sole discretion.</span>
                      </template>
                    </RmToolTip>
                    <br>
                  </div>
                  <div
                    v-if="confirmingReservation?.stripeHoldCapturedMessage"
                    class="reservation-item-spend"
                  >
                    {{ confirmingReservation.stripeHoldCapturedMessage }}
                  </div>
                </div>
                <!--                  <div class="text-caption">*RM takes no commission nor other fees</div> -->
                <!--                  <div v-if="!thisRestaurantHoldData?.holdAmountDollars" class="text-caption" style="line-height: 1em;">**spend can be held/taken in advance if preferred</div> -->
              </div>
            </div>

            <div
              v-if="!thisRestaurantDeclinedAt && (confirmingReservation.status =='Awaiting Response from Restaurant' || confirmingReservation.status =='Submitted' || confirmingReservation.status =='In Review' || confirmingReservation.status =='Member Action') && confirmingReservation.requestExpirationTime"
              class="text-left text-xs mt-1 mb-1 fine-print"
            >
              <b>Expires:</b> {{ moment(confirmingReservation.requestExpirationTime).tz(confirmingReservation.timeZone.identifier).format("M/DD [at] h:mma") }} {{ confirmingReservation.timeZone.abbreviation }}
            </div>

            <div
              v-if="confirmingReservation.owner?.userLevel == 'dummy'"
              class="text-xs"
              style="line-height: 1.1em"
            >
              <div class=" mt-4 mb-0 pa-0 text-sm">
                <u><b>Sample Member Profile & Network Spend</b></u>
              </div>
              <div class="flex align-center gap-3">
                <a
                  :href="`/m/${confirmingReservation.owner?.profileId}`"
                  target="_blank"
                  style="text-decoration: inherit; color: inherit;"
                >
                  <div class="avatar rounded-circle mr-1 ml-2">
                    <div class="avatar-fallback flex flex-row align-center justify-center">
                      <span class="avatar-fallback-text text-xl"><VIcon>mdi-user</VIcon></span>
                    </div>
                  </div>
                </a>
                <div class="user-info-content ">
                  <div class="flex">
                    <div class="flex flex-column">
                      <div>
                        <span class="text-sm font-weight-bold">Full Name</span><VIcon
                          size="16"
                          class="verified-icon ml-1"
                          style="margin: 0px; margin-top: 2px;"
                        >
                          material-symbols:verified
                        </VIcon>
                        <span class="text-xs ml-1">(Platinum)</span>
                        <div class="text-primary cursor-pointer">
                          <a
                            href=""
                            target="_blank"
                            style="text-decoration: inherit; color: inherit;"
                          >reserverm.com/m/fullname</a>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    style="line-height: 1.1em;"
                    class="text-xs"
                  >
                    <div>Position - Company</div>
                  </div>
                </div>
              </div>
              <!--                <div class="mt-1 mb-1"> -->
              <!--                  <div class="mt-1 text-xs" style="line-height: 1.1em;"> -->
              <!--                    ABC - Verified member of high profile group -->
              <!--                  </div> -->
              <!--                  <div class="mt-1 text-xs" style="line-height: 1.1em;"> -->
              <!--                    XYZ PWM - Verified UHNW client of XYZ Private Wealth Management -->
              <!--                  </div> -->
              <!--                </div> -->
              <div class=" mt-1">
                Dine out personally & professionally 2-4 times a week. Love great Cabarnets, Clase Azul, Wagyu & Bluefin Tuna.
              </div>

              <VCol class="d-flex align-center pa-0 mt-1">
                <a
                  href=""
                  target="_blank"
                >
                  <VIcon
                    size="20"
                    class="mr-3"
                  >mdi-linkedin</VIcon>
                </a>
                <a
                  href=""
                  target="_blank"
                >
                  <VIcon
                    size="20"
                    class="mr-3"
                  >mdi-instagram</VIcon>
                </a>
              </VCol>

              <div class="mt-1">
                M. (123) 456-7890 | your@email.com
              </div>
            </div>
            <div
              v-else
              class="text-xs"
              style="line-height: 1.1em"
            >
              <div
                v-if="restaurantMemberSpend || overallSpend"
                class=" mt-2 mb-0 pa-0 text-sm"
              >
                <u><b>{{ confirmingReservation.owner.fullName }}'s Profile & Network Spend</b></u>
              </div>
              <div
                v-else
                class=" mt-2 mb-0 pa-0 text-sm"
              >
                <u><b>{{ confirmingReservation.owner.fullName }}'s Profile</b></u>
              </div>
              <div class="flex align-center gap-3">
                <a
                  :href="`/m/${confirmingReservation.owner?.profileId}`"
                  target="_blank"
                  style="text-decoration: inherit; color: inherit;"
                >
                  <div class="avatar rounded-circle mr-1 ml-2">
                    <VImg
                      v-if="confirmingReservation.owner?.profileImageId"
                      class="avatar-image"
                      :src="mediaUrl(confirmingReservation.owner?.profileImageId, 'small')"
                      cover
                    />
                    <div
                      v-else
                      class="avatar-fallback flex flex-row align-center justify-center"
                    >
                      <span class="avatar-fallback-text">{{ confirmingReservation.owner?.firstName?.[0]?.toUpperCase() }}{{ confirmingReservation.owner?.lastName?.[0]?.toUpperCase() }}</span>
                    </div>
                  </div>
                </a>
                <div class="user-info-content ">
                  <div class="flex">
                    <div class="flex flex-column">
                      <div>
                        <span class="text-sm font-weight-bold">{{ confirmingReservation.owner.fullName }}</span><VIcon
                          v-if="confirmingReservation.owner?.verified"
                          size="16"
                          class="verified-icon ml-1"
                          style="margin: 0px; margin-top: 2px;"
                        >
                          material-symbols:verified
                        </VIcon>
                        <span class="text-xs ml-1">({{ confirmingReservation.owner.membershipType }})</span>
                        <div class="text-primary cursor-pointer">
                          <a
                            :href="`/m/${confirmingReservation.owner?.profileId}`"
                            target="_blank"
                            style="text-decoration: inherit; color: inherit;"
                          >reserverm.com/m/{{ confirmingReservation.owner?.profileId }}</a>
                        </div>
                      </div>
                      <!-- Favorites/Connections directly under the name -->
                      <div
                        v-if="confirmingReservation.bookingHistoryBlock"
                        class="text-xs mt-1"
                      >
                        {{ confirmingReservation.bookingHistoryBlock.memberSummaryLine }}
                      </div>
                    </div>
                  </div>
                  <!--                      <a :href="`/m/${confirmingReservation.owner?.profileId}`" target="_blank"  style="text-decoration: inherit; color: inherit;"> -->
                  <!-- &lt;!&ndash;                        &ndash;&gt; -->
                  <!--                        <span class="text-primary">{{ confirmingReservation.owner.fullName}}<v-icon v-if="confirmingReservation.owner?.verified" class="verified-icon ml-0">material-symbols:verified</v-icon>, {{ confirmingReservation.owner.membershipType }}<v-icon size="x-small">mdi-external-link</v-icon><br> -->
                  <!--                        </span> -->
                  <!--                      </a> -->
                  <a
                    v-if="confirmingReservation.owner.membershipType == 'Trial' && confirmingReservation.owner.referredBy"
                    :href="`/m/${confirmingReservation.owner?.profileId}`"
                    target="_blank"
                    style=" color: inherit;"
                    class="mt-0"
                  >
                    <b><span>Referred by {{ confirmingReservation.owner.referredBy }}</span>
                    </b>
                  </a>
                  <div
                    style="line-height: 1.1em;"
                    class="text-xs"
                  >
                    <div v-if="confirmingReservation.owner?.title && confirmingReservation.owner?.company">
                      {{ confirmingReservation.owner.title }} - {{ confirmingReservation.owner.company }}
                    </div>
                    <div v-else-if="confirmingReservation.owner?.company">
                      {{ confirmingReservation.owner.company }}
                    </div>
                    <div
                      v-if="confirmingReservation.owner?.title2 && confirmingReservation.owner?.company2"
                      style="margin-top: 0.25em;"
                    >
                      {{ confirmingReservation.owner.title2 }} - {{ confirmingReservation.owner.company2 }}
                    </div>
                    <div
                      v-else-if="confirmingReservation.owner?.company2"
                      style="margin-top: 0.25em;"
                    >
                      {{ confirmingReservation.owner.company2 }}
                    </div>
                  </div>
                </div>
              </div>
              <MemberVerifiedTags
                :member="confirmingReservation.owner"
                size="large"
                class="mt-1"
              />
              <div
                v-if="confirmingReservation.owner?.bio"
                class=" mt-1"
              >
                <b>Bio:</b> {{ confirmingReservation.owner.bio }}
              </div>

              <!--                <div v-if="confirmingReservation.owner?.profileId" class="mt-1"> -->
              <!--                  RM: <a @click="isViewAndShareProfileDialogOpen = true;" target="_blank" class="cursor-pointer"> -->
              <!--                  reserverm.com/m/{{confirmingReservation.owner?.profileId}} -->
              <!--                </a> -->
              <!--                </div> -->
              <!--                <div v-if="confirmingReservation.owner?.profileWebsiteLink" class=""> -->
              <!--                  Bio page: <a :href="getFullUrl(confirmingReservation.owner?.profileWebsiteLink)" target="_blank" > -->
              <!--                  {{confirmingReservation.owner?.profileWebsiteLink}} -->
              <!--                </a> -->
              <!--                </div> -->
              <!--                <div v-if="confirmingReservation.owner?.profileLinkedInProfileId" class=""> -->
              <!--                  LinkedIn: <a :href="buildProfileUrl('linkedin', confirmingReservation.owner?.profileLinkedInProfileId)" target="_blank" > -->
              <!--                  linkedin.com/in/{{confirmingReservation.owner?.profileLinkedInProfileId}} -->
              <!--                </a> -->
              <!--                </div> -->
              <!--                <div v-if="confirmingReservation.owner?.profileInstagramProfileId" class=""> -->
              <!--                  Instagram: <a :href="buildProfileUrl('instagram', confirmingReservation.owner?.profileInstagramProfileId)" target="_blank" > -->
              <!--                  instagram.com/{{confirmingReservation.owner?.profileInstagramProfileId}} -->
              <!--                </a> -->
              <!--                </div> -->
              <!--                <div v-if="confirmingReservation.owner?.profileXProfileId" class=""> -->
              <!--                  X: <a :href="buildProfileUrl('twitter', confirmingReservation.owner?.profileXProfileId)" target="_blank" > -->
              <!--                  twitter.com/{{confirmingReservation.owner?.profileXProfileId}} -->
              <!--                </a> -->
              <!--                </div> -->
              <!--                <div v-if="confirmingReservation.owner?.profileTikTokProfileId" class=""> -->
              <!--                  TikTok: <a :href="buildProfileUrl('tiktok', confirmingReservation.owner?.profileTikTokProfileId)" target="_blank" > -->
              <!--                  tiktok.com/@{{confirmingReservation.owner?.profileTikTokProfileId}} -->
              <!--                </a> -->
              <!--                </div> -->

              <VCol class="d-flex align-center pa-0 mt-1">
                <!--                  <a v-if="confirmingReservation.owner?.profileId" :href="`/m/${confirmingReservation.owner?.profileId}`" target="_blank" style="height:20px;"> -->
                <!--                    <img :src="socialRmLogo" height="20" class="mr-3"/> -->
                <!--                  </a> -->
                <a
                  v-if="confirmingReservation.owner?.profileLinkedInProfileId"
                  :href="buildProfileUrl('linkedin', confirmingReservation.owner?.profileLinkedInProfileId)"
                  target="_blank"
                >
                  <VIcon
                    size="20"
                    class="mr-3"
                  >mdi-linkedin</VIcon>
                </a>
                <a
                  v-if="confirmingReservation.owner?.profileInstagramProfileId"
                  :href="buildProfileUrl('instagram', confirmingReservation.owner?.profileInstagramProfileId)"
                  target="_blank"
                >
                  <VIcon
                    size="20"
                    class="mr-3"
                  >mdi-instagram</VIcon>
                </a>
                <a
                  v-if="confirmingReservation.owner?.profileXProfileId"
                  :href="buildProfileUrl('twitter', confirmingReservation.owner?.profileXProfileId)"
                  target="_blank"
                >
                  <VIcon
                    size="20"
                    class="mr-3"
                  >simple-icons:x</VIcon>
                </a>
                <a
                  v-if="confirmingReservation.owner?.profileTikTokProfileId"
                  :href="buildProfileUrl('tiktok', confirmingReservation.owner?.profileTikTokProfileId)"
                  target="_blank"
                >
                  <VIcon
                    size="20"
                    class="mr-3"
                  >simple-icons:tiktok</VIcon>
                </a>
                <a
                  v-if="confirmingReservation.owner?.profileWebsiteLink"
                  :href="getFullUrl(confirmingReservation.owner?.profileWebsiteLink)"
                  target="_blank"
                >
                  <VIcon
                    size="20"
                    class="mr-3"
                  >mdi-web</VIcon>
                </a>
              </VCol>
              <!--                <div class="mt-2" v-if="confirmingReservation.hostAffiliated"> -->
              <!--                  <b>Affiliation with {{thisRestaurant}}:</b> -->

              <!--                  <div class="text-sm ml-2"> -->
              <!--                    {{confirmingReservation.hostAffiliation}} -->
              <!--                  </div> -->
              <!--                </div> -->
              <div class=" mt-1 mb-0 pa-0 text-xs">
                <span v-if=" !confirmedByDifferentRestaurant && !thisRestaurantDeclinedAt && confirmingReservation.status !='Rescinded' && confirmingReservation.status !='No Response'">M. {{ displayPhone(confirmingReservation.owner.phoneNumber) }}<br>E. {{ confirmingReservation.owner.email }}</span>
              </div>
            </div>

            <div
              v-if="confirmingReservation.bookingHistoryBlock"
              class="mt-2 mb-1"
            >
              <!-- Bookings + in-group favorites (memberSummaryLine renders under the name above) -->
              <div
                v-if="confirmingReservation.bookingHistoryBlock.bookingHeading"
                class="mt-2"
              >
                <div class="text-sm">
                  <u><b>{{ confirmingReservation.bookingHistoryBlock.bookingHeading }}</b></u>
                </div>
                <div
                  v-for="(line, lineIdx) in confirmingReservation.bookingHistoryBlock.bookingLines"
                  :key="`bh-line-${lineIdx}`"
                  class="text-xs"
                >
                  {{ line }}
                </div>
                <div
                  v-if="confirmingReservation.bookingHistoryBlock.fanLine"
                  class="text-xs"
                >
                  {{ confirmingReservation.bookingHistoryBlock.fanLine }}
                </div>
                <div class="text-xs mt-1 text-medium-emphasis fine-print">
                  (full details viewable on RM)
                </div>
              </div>
            </div>

            <div v-if="restaurantMemberSpend || overallSpend">
              <div class="spend-table-holder mt-1 mb-1">
                <div class="mt-2 mb-0 pa-0 text-xs text-center">
                  <b><template v-if="confirmingReservation.owner?.userLevel != 'dummy'">{{ confirmingReservation.owner.firstName }}'s </template>Spend (last 12 months via RM) - <span
                    class="text-xs text-center cursor-pointer text-primary"
                    style="margin-top: 2px;"
                    @click="isShowingSpendDetailsDialog = !isShowingSpendDetailsDialog"
                  >view spend details</span></b>
                </div>
                <table>
                  <thead>
                    <tr>
                      <th rowspan="2" />
                      <th colspan="2">
                        {{ confirmingReservation.owner.firstName || 'Member' }}
                      </th>
                      <th colspan="2">
                        {{ confirmingReservation.owner.firstName || 'Member' }} + Network
                      </th>
                    </tr>
                    <tr>
                      <th>Bookings</th>
                      <th>Spend</th>
                      <th>Bookings</th>
                      <th>Spend</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="restaurantMemberSpend">
                      <td>{{ confirmingReservation.confirmimgRestaurant.name }}</td>
                      <td>{{ restaurantMemberSpend.bookings.length || '-' }}</td>
                      <td>{{ formatCurrency(restaurantMemberSpend.total) }}</td>
                      <td>{{ restaurantNetworkSpend.count || '-' }}</td>
                      <td>{{ formatCurrency(restaurantNetworkSpend.total) }}</td>
                    </tr>
                    <tr v-if="groupMemberSpend && confirmingReservation.confirmimgRestaurant.group">
                      <td>{{ confirmingReservation.confirmimgRestaurant.group }}</td>
                      <td>{{ groupMemberSpend.bookings.length || '-' }}</td>
                      <td>{{ formatCurrency(groupMemberSpend.total) }}</td>
                      <td>{{ groupNetworkSpend.count || '-' }}</td>
                      <td>{{ formatCurrency(groupNetworkSpend.total) }}</td>
                    </tr>
                    <tr v-if="overallSpend">
                      <td>Overall</td>
                      <td>{{ overallSpend.memberTotalCount || '-' }}</td>
                      <td>{{ formatCurrency(overallSpend.memberTotalSpend) }}</td>
                      <td>{{ overallSpend.networkTotalCount || '-' }}</td>
                      <td>{{ formatCurrency(overallSpend.networkTotalSpend) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <template v-if="confirmingReservation">
            <!--              <div v-if="responseAllowed"> -->
            <!--                <v-alert v-if="!confirmingReservation.confirmedRestaurant && otherRestaurants && !thisRestaurantDeclinedAt" class="ma-2" type="info"> -->
            <!--                  Member requested <b>{{addAndToString(otherRestaurants)}}</b> as equally preferred options so referral has been sent to them too. First restaurant to confirm will secure referral. -->
            <!--                </v-alert> -->
            <!--              </div> -->
            <div v-if="!responseAllowed">
              <VAlert
                v-if="confirmingReservation.status == 'Accepted' && confirmedByThisRestaurant && !afterReservationStartTime"
                class="ma-2"
                type="info"
              >
                Thanks for confirming! Member has been sent an automated confirmation from RM too, in addition to the one they'll receive from you via {{ confirmingReservation.confirmedReservationSystem }}
              </VAlert>
              <VAlert
                v-if="confirmingReservation.status == 'Accepted' && confirmedByThisRestaurant && afterReservationStartTime"
                class="ma-2"
                type="info"
              >
                Thank you for hosting our member! Please contact us at <a
                  href="mailto:contact@reserverm.com"
                  rel="noopener noreferrer"
                  class="text-primary"
                >contact@reserverm.com</a> with any questions or concerns.
              </VAlert>

              <!--                <v-alert v-if="confirmedByDifferentRestaurant && !thisRestaurantDeclinedAt" class="ma-2" type="info"> -->
              <!--                  This request was also sent to <b>{{confirmingReservation.confirmedRestaurant}}</b> as an equally preferred option, and they have already confirmed. -->
              <!--                </v-alert> -->

              <!--                <v-alert v-if="thisRestaurantDeclinedAt" class="ma-2" type="info"> -->
              <!--                  You have declined this reservation. Thanks for your consideration! -->
              <!--                </v-alert> -->

              <VAlert
                v-if="confirmingReservation.status == 'Canceling' && confirmedByThisRestaurant"
                class="ma-2"
                type="info"
              >
                The member wishes to cancel this reservation! Please process the cancellation if possible.
              </VAlert>

              <VAlert
                v-if="confirmingReservation.status == 'Canceled' && confirmedByThisRestaurant"
                class="ma-2"
                type="info"
              >
                The member wishes to cancel this reservation! Please process the cancellation if possible.
              </VAlert>

              <VAlert
                v-else-if="confirmingReservation.status == 'Canceling'"
                class="ma-2"
                type="info"
              >
                This reservation is being canceled.
              </VAlert>

              <VAlert
                v-else-if="confirmingReservation.status == 'Rescinding'"
                class="ma-2"
                type="info"
              >
                This reservation is being rescinded, please disregard it.
              </VAlert>

              <VAlert
                v-else-if="confirmingReservation.status == 'Rescinded'"
                class="ma-2"
                type="info"
              >
                This reservation is rescinded, please disregard it.
              </VAlert>
            </div>
          </template>
        </div>

        <template v-if="!previewMode && myUser?.userLevel === 'admin' && confirmingReservation && (confirmingReservation.status =='Accepted') && confirmingReservation.stripeHoldConfirmedAmount && !confirmingReservation.stripeHoldCapturedAmount && !confirmingReservation.stripeHoldReleasedAt">
          <div
            v-if="!afterReservationStartTime"
            class="mb-2"
          >
            <VBtn
              class="ml-2"
              :disabled="previewMode || saving"
              @click="releaseHold"
            >
              Release Hold
            </VBtn>
            <VBtn
              class="ml-2"
              :disabled="previewMode || saving"
              @click="releaseHoldAndCancel"
            >
              Release Hold & Cancel Reservation
            </VBtn>
          </div>
          <template v-else>
            <VCol
              cols="12"
              class="pt-0 pb-0"
            >
              <span class="text-caption">If no action is taken below, the hold will be release 24 hours after the reservation</span>
              <h3>Please choose one:</h3>
            </VCol>
            <VRadioGroup
              v-model="postChoiceOne"
              class="ml-4 mt-0"
            >
              <VRadio value="metMinimum">
                <template #label>
                  <div>Member met/exceeded the minimum spend</div>
                </template>
              </VRadio>
              <VRadio value="didNotMeetMinimum">
                <template #label>
                  <div>Member did not meet minimum spend</div>
                </template>
              </VRadio>
              <VRadio value="noShow">
                <template #label>
                  <div>Member was a no-show</div>
                </template>
              </VRadio>
            </VRadioGroup>
          </template>
        </template>
        <template v-if="confirmingReservation && (confirmingReservation.status =='Canceled') && confirmingReservation.stripeHoldConfirmedAmount && !confirmingReservation.stripeHoldCapturedAmount && !confirmingReservation.stripeHoldReleasedAt">
          <VTextField
            v-model="captureAmount"
            class="mt-4"
            :label="`Amount to charge ($${numberWithCommas((confirmingReservation.stripeHoldConfirmedAmount).toFixed(2))} max)`"
            autocomplete="null"
            type="number"
            prefix="$"
            @input="checkCaptureAmount"
          />
          <div class="mt-2">
            <VBtn
              :disabled="previewMode || !captureAmount || saving"
              class="mr-2"
              @click="captureHoldCanceled"
            >
              Charge ${{ captureAmount }}
            </VBtn>
            OR
            <VBtn
              class="ml-2"
              :disabled="previewMode || saving"
              @click="releaseHoldAndCancel"
            >
              Release Hold
            </VBtn>
          </div>
        </template>

        <div
          v-if="postChoiceOne=='metMinimum'"
          class="mt-2 mb-2"
        >
          <VBtn
            class="ml-2"
            :disabled="previewMode || saving"
            @click="releaseHold"
          >
            Release Hold
          </VBtn>
        </div>
        <div v-else-if="postChoiceOne">
          <VTextField
            v-model="captureAmount"
            class="mt-4 ml-2"
            :label="`Amount to charge ($${numberWithCommas((confirmingReservation.stripeHoldConfirmedAmount).toFixed(2))} max)`"
            autocomplete="null"
            type="number"
            prefix="$"
            @input="checkCaptureAmount"
          />
          <VTextField
            v-if="postChoiceOne=='didNotMeetMinimum'"
            v-model="captureMessage"
            class="mt-4 ml-2"
            label="Briefly explain to member why you are charging them"
            :rules="[requiredValidator]"
            required
          />
        </div>
        <div
          v-if="postChoiceOne=='didNotMeetMinimum'"
          class="mt-2 mb-2 ml-2"
        >
          <VBtn
            :disabled="previewMode || !captureAmount || saving"
            class="mr-2"
            @click="captureHold"
          >
            Charge ${{ captureAmount }}
          </VBtn>
          OR
          <VBtn
            class="ml-2"
            :disabled="previewMode || saving"
            @click="releaseHold"
          >
            Release Hold
          </VBtn>
        </div>
        <div
          v-else-if="postChoiceOne=='noShow'"
          class="mt-2 mb-2 ml-2"
        >
          <VBtn
            :disabled="previewMode || !captureAmount || saving"
            class="mr-2"
            @click="captureHoldNoShow"
          >
            Charge ${{ captureAmount }}
          </VBtn>
          OR
          <VBtn
            class="ml-2"
            :disabled="previewMode || saving"
            @click="releaseHoldAndCancel"
          >
            Release Hold
          </VBtn>
        </div>


        <VDivider
          v-if="responseAllowed || isPreviewDraft"
          class="ma-2"
        />
        <div
          v-if="responseAllowed || isPreviewDraft"
          ref="highlightedDiv"
          class="text-xs"
          style="border-radius: 6px;"
          :class="{ highlight: isHighlighted }"
        >
          <VForm
            ref="refVForm"
            @submit.prevent="onFormSubmit"
          >
            <!-- Accept — always open; it's the goal, so it takes no extra step -->
            <div class="response-block mb-6">
              <div class="text-sm mb-2">
                <b>Accept</b>
              </div>
              <VRadioGroup
                v-model="selectedTime"
                class="ml-4"
                style="margin-bottom: 0px !important;"
                inline
              >
                <VRadio
                  v-for="timeOption in timeOptions"
                  :key="timeOption"
                  :value="timeOption"
                  class="text-sm"
                  density="compact"
                  @click="scrollToBottom"
                >
                  <template #label>
                    <div
                      v-if="timeOption == confirmingReservation.preferredTime"
                      class="text-sm"
                    >
                      <b>{{ timeOption }} (Preferred)</b>
                    </div>
                    <div
                      v-else
                      class="text-sm"
                    >
                      {{ timeOption }}
                    </div>
                  </template>
                </VRadio>
              </VRadioGroup>


              <VCol
                v-if="thisRestaurantHoldData?.holdAmountDollars && confirmingReservation?.stripeHoldMaxAmount"
                cols="12"
              >
                <!--                  <V-checkbox -->
                <!--                      v-model="takeHold" -->
                <!--                      :label="`Waive $${numberWithCommas((confirmingReservation.stripeHoldMaxAmount).toFixed(2))} hold`" -->
                <!--                  /> -->
                <VSwitch
                  v-model="takeHold"
                  style="margin-bottom: 0px !important;"
                  color="primary"
                >
                  <template #label>
                    <div class="text-sm">
                      {{ (takeHold ? `Keep spend ($${numberWithCommas((confirmingReservation.stripeHoldMaxAmount).toFixed(2))}) on hold` : `Waive $${numberWithCommas((confirmingReservation.stripeHoldMaxAmount).toFixed(2))} hold`) }}
                    </div>
                  </template>
                </VSwitch><br>
                <RmToolTip>
                  <template #text>
                    <span class="text-primary">how it works</span>
                  </template>
                  <template #description>
                    <span>Member has authorized to be charged the full F&B if not met. Credit card hold can be taken in advance if preferred. Restaurants can waive minimum at their sole discretion.</span>
                  </template>
                </RmToolTip>
              </VCol>

              <div>
                <VTextField
                  v-model="confirmedByName"
                  label="Confirmed by (your name)"
                  :rules="responseTab === 'Accept' ? [requiredValidator] : []"
                  density="compact"
                  class="custom-font-size"
                />
              </div>
              <div>
                <VSelect
                  v-model="confirmedReservationSystem"
                  label="Member will receive confirmation from"
                  :items="['OpenTable', 'Resy', 'SevenRooms', 'Tock', 'Tripleseat', 'Other']"
                  :rules="responseTab === 'Accept' ? [requiredValidator] : []"
                  density="compact"
                />
              </div>
              <div>
                <VTextarea
                  v-model="confirmedMessageToMember"
                  autocomplete="none"
                  :label="`Message to ${confirmingReservation.owner.firstName} (Optional)`"
                  hint="Reservation confirmation code &/or anything else"
                  density="compact"
                  rows="3"
                  auto-grow
                />
              </div>

              <VBtn
                color="primary"
                variant="flat"
                size="large"
                block
                :disabled="previewMode"
                class="mt-3"
                @click="submitAs('Accept')"
              >
                Accept
              </VBtn>
            </div>

            <!-- Provide alternates / Decline — secondary -->
            <div class="response-block">
              <VRadioGroup
                v-model="declineType"
                class="ml-2"
                style="margin-bottom: 0px !important;"
                hide-details
              >
                <div class="text-sm mb-1">
                  <b>Provide alternates</b>
                </div>
                <VRadio
                  key="HigherSpend"
                  value="HigherSpend"
                  class="text-sm"
                  density="compact"
                >
                  <template #label>
                    <div class="text-sm">
                      Require higher minimum
                    </div>
                  </template>
                </VRadio>
                <VSelect
                  v-if="declineType == 'HigherSpend'"
                  v-model="declineMessageHigherSpend"
                  class="ml-6 mt-2"
                  :label="higherSpendLabel"
                  item-title="text"
                  item-value="value"
                  :items="minSpendOptions"
                  :rules="responseTab === 'Decline' ? [requiredValidator] : []"
                  hint="Provide an amount at which you would be willing to accept if member resubmits"
                  persistent-hint
                  style="margin-bottom: 1em !important; min-width: 250px; width: 100%; max-width: 300px;"
                  density="compact"
                />
                <VRadio
                  key="DifferentTime"
                  value="DifferentTime"
                  class="text-sm"
                  density="compact"
                >
                  <template #label>
                    <div class="text-sm">
                      Provide alternate times
                    </div>
                  </template>
                </VRadio>
                <VTextField
                  v-if="declineType == 'DifferentTime'"
                  v-model="declineMessageDifferentTime"
                  label="Alternate time(s)"
                  hint="Provide alternate time(s) which could be accommodated if member resubmits"
                  persistent-hint
                  class="ml-6 mt-2"
                  density="compact"
                />

                <div class="text-sm mt-4 mb-1">
                  <b>Decline</b>
                </div>
                <VRadio
                  key="Decline"
                  value="Decline"
                  class="text-sm"
                  density="compact"
                >
                  <template #label>
                    <div class="text-sm">
                      Decline this request
                    </div>
                  </template>
                </VRadio>
                <VTextarea
                  v-if="declineType == 'Decline'"
                  v-model="declineMessageToMember"
                  autocomplete="none"
                  :label="`Message to ${confirmingReservation.owner.firstName}`"
                  hint="Optional"
                  persistent-hint
                  density="compact"
                  rows="3"
                  auto-grow
                  class="ml-6 mt-2"
                />
              </VRadioGroup>

              <VCol
                v-if="declineType"
                cols="12"
                class="d-flex justify-center mt-3"
              >
                <VBtn
                  variant="outlined"
                  :disabled="previewMode"
                  @click="submitAs('Decline')"
                >
                  {{ declineType === 'HigherSpend' ? 'Require higher minimum' : declineType === 'DifferentTime' ? 'Provide alternate time(s)' : 'Decline' }}
                </VBtn>
              </VCol>
            </div>
            <div
              v-if="previewMode"
              class="text-caption text-center"
            >
              Preview — only {{ confirmingReservation.confirmimgRestaurant?.name || 'the restaurant' }} can respond here
            </div>
          </VForm>
          <!-- For the restaurant, not the member previewing its page (RM-360) -->
          <div
            v-if="!previewMode"
            class="text-caption text-center"
          >
            Activate free account to connect with your fans:<br><a
              href="https://reserverm.com/restaurants"
              target="_blank"
            >reserverm.com/restaurants</a>
          </div>
        </div>
      </div>
        </fieldset>
      </VDefaultsProvider>
    </div>
    <VDialog
      max-width="450"
      :model-value="isShowingSpendDetailsDialog"
    >
      <VCard class="px-5 py-6 pb-0">
        <div class="text-center text-sm ">
          <b>{{ confirmingReservation.owner.firstName }}'s Network's Spend<br>(last 12 months via RM)</b>
        </div>

        <div
          style="display: flex;"
          class="justify-center"
        >
          <VTabs
            v-model="spendDialogTab"
            class="tab-height"
            center-active
          >
            <VTab
              v-if="thisRestaurantSpendShareSettings.includeSpendRestaurantAndGroup"
              value="restaurant"
              class="tab-height"
            >
              Restaurant
            </VTab>
            <VTab
              v-if="thisRestaurantSpendShareSettings.includeSpendRestaurantAndGroup"
              value="group"
              class="tab-height"
            >
              Group
            </VTab>
            <VTab
              v-if="confirmingReservation.includeSpendOverall"
              value="overall"
              class="tab-height"
            >
              Overall
            </VTab>
          </VTabs>
        </div>
        <VDivider />
        <VWindow
          v-model="spendDialogTab"
          :touch="false"
        >
          <VWindowItem
            class="pt-4 text-xs"
            value="restaurant"
          >
            <div class="scrollable-dialog-content">
              <div class="text-xs">
                <div>
                  <b>{{ confirmingReservation.confirmimgRestaurant.name }}: </b>
                  <!--            <span v-if="restaurantMemberSpend.bookings.length > 0">{{confirmingReservation.owner.firstName}}, {{restaurantMemberSpend.bookings.length}} bookings, <b>{{formatCurrency(restaurantMemberSpend.total)}}</b></span> -->
                  <!--            <span v-if="restaurantNetworkSpend.networkMembers.length > 0"> <span v-if="restaurantMemberSpend.bookings.length > 0"> |</span> {{confirmingReservation.owner.firstName}}'s Network, {{restaurantNetworkSpend.count}} bookings, <b>{{formatCurrency(restaurantNetworkSpend.total)}}</b></span> -->
                  <!--            <v-btn density="compact" size="small" class="restaurant-hours-toggle" variant="text" color="secondary" :icon="!showRestaurantSpendLineItems?'mdi-chevron-down':'mdi-chevron-up'" ></v-btn> -->
                </div>
                <div class="ml-2 mb-2">
                  <div class="ml-2">
                    <div v-if="restaurantMemberSpend.bookings.length > 0">
                      <b>{{ confirmingReservation.owner.firstName }}: {{ formatCurrency(restaurantMemberSpend.total) }} ({{ restaurantMemberSpend.bookings.length }} booking{{ (restaurantMemberSpend.bookings.length > 1 ? 's' : '') }})</b>
                    </div>
                    <div v-for="item in restaurantMemberSpend.bookings">
                      <div class="ml-2">
                        {{ moment(item.date).format("ddd, M/D") }} | {{ formattedSpend(item) }}
                      </div>
                    </div>
                    <template v-if="restaurantNetworkSpend.networkMembers.length > 0">
                      <div :class="{'mt-2': restaurantMemberSpend.bookings.length > 0}">
                        <b>{{ confirmingReservation.owner.firstName }}'s Network: {{ formatCurrency(restaurantNetworkSpend.total) }} ({{ restaurantNetworkSpend.count }} booking{{ (restaurantNetworkSpend.count > 1 ? 's' : '') }})</b>
                      </div>
                      <div
                        v-for="item in restaurantNetworkSpend.networkMembers"
                        v-if="restaurantNetworkSpend.networkMembers > 10"
                      >
                        <div class="ml-2">
                          {{ item.fullName }} ({{ item.primaryGroupName }})
                        </div>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
              <div class="text-center text-xs mb-2">
                <i>Access more data on your restaurant account</i>
              </div>
            </div>
          </VWindowItem>

          <VWindowItem
            class=""
            value="group"
          >
            <div class="scrollable-dialog-content">
              <div
                v-if="confirmingReservation.confirmimgRestaurant.group"
                class="text-xs mt-0"
              >
                <div>
                  <b>{{ confirmingReservation.confirmimgRestaurant.group }}: </b>
                  <!--            <span v-if="groupMemberSpend.bookings.length > 0">{{confirmingReservation.owner.firstName}}, {{groupMemberSpend.bookings.length}} bookings, <b>{{formatCurrency(groupMemberSpend.total)}}</b></span> -->
                  <!--            <span v-if="groupNetworkSpend.networkMembers.length > 0"> <span v-if="restaurantMemberSpend.bookings.length > 0"> |</span> {{confirmingReservation.owner.firstName}}'s Network, {{groupNetworkSpend.count}} bookings, <b>{{formatCurrency(groupNetworkSpend.total)}}</b></span> -->
                  <!--            <v-btn density="compact" size="small" class="restaurant-hours-toggle" variant="text" color="secondary" :icon="!showGroupSpendLineItems?'mdi-chevron-down':'mdi-chevron-up'" ></v-btn> -->
                </div>
                <div
                  v-if="(groupMemberSpend.bookings.length > 0 || groupNetworkSpend.networkMembers.length > 0)"
                  class="ml-2 mb-2"
                >
                  <div class="ml-2">
                    <div v-if="groupMemberSpend.bookings.length > 0">
                      <b>{{ confirmingReservation.owner.firstName }}: {{ formatCurrency(groupMemberSpend.total) }} ({{ groupMemberSpend.bookings.length }} booking{{ (groupMemberSpend.bookings.length > 1 ? 's' : '') }})</b>
                    </div>
                    <div v-for="item in groupMemberSpend.bookings">
                      <div class="ml-2">
                        {{ item.confirmedRestaurant }}, {{ moment(item.date).format("ddd, M/D") }} | {{ formattedSpend(item) }}
                      </div>
                    </div>
                    <template v-if="groupNetworkSpend.networkMembers.length > 0">
                      <div :class="{'mt-2': restaurantMemberSpend.bookings.length > 0}">
                        <b>{{ confirmingReservation.owner.firstName }}'s Network: {{ formatCurrency(groupNetworkSpend.total) }} ({{ groupNetworkSpend.count }} booking{{ (groupNetworkSpend.count > 1 ? 's' : '') }})</b>
                      </div>
                      <div
                        v-for="item in groupNetworkSpend.networkMembers"
                        v-if="groupNetworkSpend.networkMembers.length > 10"
                      >
                        <div class="ml-2">
                          {{ item.fullName }} ({{ item.primaryGroupName }})
                        </div>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
              <div class="text-center text-xs mb-2">
                <i>Access more data on your group account</i>
              </div>
            </div>
          </VWindowItem>
          <VWindowItem
            class=""
            value="overall"
          >
            <div class="scrollable-dialog-content">
              <div
                v-if="overallSpend.networkTotalCount > 0"
                class="text-xs mt-0"
              >
                <div><b>Overall: </b></div>
                <div
                  v-if="(groupMemberSpend.bookings.length > 0 || groupNetworkSpend.networkMembers.length > 0)"
                  class="ml-2 mb-2"
                >
                  <div class="ml-2">
                    <div v-if="overallSpend.memberTotalCount > 0">
                      <b>{{ confirmingReservation.owner.firstName }}: {{ formatCurrency(overallSpend.memberTotalSpend) }} ({{ overallSpend.memberTotalCount }} booking{{ (overallSpend.memberTotalCount > 1 ? 's' : '') }})</b>
                    </div>
                    <div v-if="overallSpend.networkTotalCount > 0">
                      <b>{{ confirmingReservation.owner.firstName }}'s Network: {{ formatCurrency(overallSpend.networkTotalSpend) }} ({{ overallSpend.networkTotalCount }} booking{{ (overallSpend.networkTotalCount > 1 ? 's' : '') }})</b>
                    </div>
                  </div>
                </div>
              </div>
              <div class="text-center text-xs mb-2">
                <i>Verified by RM</i>
              </div>
            </div>
          </VWindowItem>
        </VWindow>
      </VCard>
      <VBtn
        icon="$close"
        size="large"
        variant="text"
        color="secondary"
        class="dialog-close-btn"
        @click="isShowingSpendDetailsDialog = false"
      >
        <VIcon>mdi-close</VIcon>
      </VBtn>
    </VDialog>
  </div>
</template>

<style lang="scss" scoped>
// RM-360: the member's preview wraps the page in a disabled fieldset.
.preview-fieldset {
  border: 0;
  margin: 0;
  min-inline-size: 0;
  padding: 0;
}

.dialog-close-btn {
    position: fixed;
    right: 6px;
    top: 6px;
  }

  .strikethrough {
    text-decoration: line-through;
  }

  .avatar {
    width: 3.5rem;
    height: 3.5rem;
    flex-shrink: 0;
    background: var(--ds-brand-primary-light);
    outline: thin solid rgba(69, 182, 169, 0.25);
    outline-offset: 0.25rem;
    margin: 0.5rem 0;
  }

  @media screen and (min-width: 670px) {
    .avatar {
      width: 3.5rem;
      height: 3.5rem;
      outline-offset: 0.25rem;
      margin: 0.5rem 0;
    }
  }

  .avatar-fallback {
    width: 100%;
    height: 100%;
    font-size: 0.8rem;
  }

  .avatar-image {
    border-radius: 50%;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media screen and (min-width: 670px) {
    .avatar-fallback {
      font-size: 0.8rem;
    }
  }

  .avatar-fallback-text {
    width: fit-content;
  }

  .user-info-content {
    //flex-basis: 15rem;
  }


  .verified-icon {
    color: rgb(0, 149, 246);
    width: 1.25rem;
    height: 1.25rem;
    bottom: 3px;
  }


  //.bottom-buttons {
  //  margin-top: 1.6rem;
  //}
  //
  //:has(.v-input__details) ~ .bottom-buttons {
  //  margin-top: -0.1rem;
  //}

  //.v-messages {
  //  font-size: 3px !important;
  //}

  //.v-input:not(:has(.v-input__details)) {
  //  margin-bottom: 1.6rem;
  //}
  .spend-table-holder {
    max-width: 100%;
    overflow-x: auto;
  }
  table {
    border-collapse: collapse;
    //width: 80%;
    //margin: auto;
  }
  th, td {
    border: 1px solid #ddd;
    padding: 8px;
    text-align: center;
  }
  th {
    background-color: rgb(69, 181, 169);
    color: white;
  }
  tr:nth-child(even) {
    background-color: #f2f2f2;
  }


  .v-tab.v-tab {
    min-width: unset;
    padding-left: 0.6rem;
    padding-right: 0.6rem;
    font-size: 0.9rem;
  }

  .small-label{
    font-size: 0.8rem !important;
  }

  .highlight {
    animation: highlight-animation 2s ease-in-out;
  }

  @keyframes highlight-animation {
    0% { background-color: rgb(69, 181, 169); }
    100% { background-color: transparent; }
  }
  :deep(.custom-font-size .v-input__control .v-field__input input ){
    font-size: 16px !important;
  }

  :deep(.v-field-label ){
    font-size: 16px !important;
  }

  :deep(.v-select__selection-text){
    font-size: 16px !important;
  }
  :deep(.v-field__input){
    font-size: 16px !important;
  }

  .scrollable-dialog-content{
    height: 600px; /* Adjust height as needed */
    overflow-y: auto;
  }

  /* Mobile-first readability: restaurants review these on a phone, so bring the
     body text up to 16px and let weight (bold labels) carry hierarchy rather
     than tiny type. Form inputs are already 16px (iOS no-zoom). */
  .text-sm { font-size: 16px !important; line-height: 1.5 !important; }
  .text-xs { font-size: 16px !important; line-height: 1.5 !important; }

  /* labeled request-detail rows — one per line with breathing room */
  .detail-line { margin-bottom: 7px; line-height: 1.45; }

  /* fine print (timestamps, helper captions) — a notch smaller than the 16px body */
  .fine-print { font-size: 14px !important; }
</style>

<route lang="yaml">
meta:
  layout: none
  action: read
  subject: Auth
</route>
